// ============================================
// LoginPage.js — Unified Dual Theme Architecture
// Fully Responsive with Hidden Scrollbars
// ============================================

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { loginUser, googleLogin, clearError } from '../redux/slices/authSlice';
import { auth } from '../config/firebase';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';

const LoginPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, isLoading, error, user, token } = useSelector((state) => state.auth);
    const { theme, toggleTheme } = useTheme();

    const isDriverPath = location.pathname.includes('driver');
    const [role, setRole] = useState(() => {
        if (isDriverPath) return 'driver';
        const last = localStorage.getItem('lastRole');
        if (last === 'pa') return 'pa';
        return 'driver';
    });
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const toast = useToast();
    // Contact Admin flow (for blocked accounts)
    const [showContactForm, setShowContactForm] = useState(false);
    const [contactMsg, setContactMsg] = useState('');
    const [contactName, setContactName] = useState('');
    const [contactLoading, setContactLoading] = useState(false);
    const [contactSent, setContactSent] = useState(false);

    // Redirect after successful login
    useEffect(() => {
        if (isAuthenticated && user) {
            const routes = { admin: '/admin', dispatcher: '/dispatcher', driver: '/driver', user: '/user', pa: '/pa' };
            navigate(routes[user.role] || '/login', { replace: true });
        }
    }, [isAuthenticated, user, navigate]);

    // Clear Redux error on component unmount
    useEffect(() => () => dispatch(clearError()), [dispatch]);

    // Toast notifications for Redux errors
    useEffect(() => {
        if (error) {
            if (error.toLowerCase().includes('block')) {
                toast.error(
                    <div className="flex flex-col gap-1 py-1 px-1">
                        <span className="font-bold tracking-wide text-rose-800 dark:text-rose-200">Account Blocked</span>
                        <span className="text-[12px] opacity-90 leading-relaxed text-rose-700 dark:text-rose-300">
                            Your access is restricted. Please{' '}
                            <button type="button" onClick={() => setShowContactForm(true)} className="underline decoration-2 underline-offset-2 hover:text-rose-900 dark:hover:text-rose-100 transition-colors font-bold pointer-events-auto cursor-pointer">
                                contact the admin
                            </button>
                            {' '}for an appeal.
                        </span>
                    </div>,
                    5000
                );
            } else {
                toast.error(error === 'Login failed' ? 'Invalid email or password' : error);
            }
            dispatch(clearError());
        }
    }, [error, dispatch, toast]);

    // If token exists and we're still loading user profile, show spinner
    if (token && isLoading) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-slate-50 dark:bg-[#050510]">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-teal-500/20 border-t-teal-500 rounded-full animate-spin" />
                    <p className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Loading...</p>
                </div>
            </div>
        );
    }

    const handleRoleToggle = (newRole) => {
        setRole(newRole);
        localStorage.setItem('lastRole', newRole);
        dispatch(clearError());
    };

    const handleChange = (e) => {
        if (error) dispatch(clearError());
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        dispatch(clearError());

        if (!formData.email.trim()) {
            return toast.error('Please provide your email address.');
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            return toast.error("Please enter a valid email address.");
        }
        if (!formData.password) {
            return toast.error('Please enter your password.');
        }
        if (formData.password.length < 8) {
            return toast.error('Password must be at least 8 characters');
        }

        dispatch(loginUser({ ...formData, loginRole: role }));
    };

    const handleGoogleSignIn = async () => {
        const provider = new GoogleAuthProvider();
        try {
            const result = await signInWithPopup(auth, provider);
            const idToken = await result.user.getIdToken();
            dispatch(googleLogin({ firebaseToken: idToken, role: role === 'pa' ? 'pa' : role === 'driver' ? 'driver' : 'user' }));
        } catch (err) {
            console.error(err.message);
        }
    };



    const handleContactAdmin = async () => {
        if (!contactMsg.trim() || !formData.email) return;
        setContactLoading(true);
        try {
            const { default: axios } = await import('axios');
            const { API_BASE_URL } = await import('../constants');
            await axios.post(`${API_BASE_URL}/auth/contact-admin`, {
                email: formData.email,
                name: contactName || formData.email.split('@')[0],
                message: contactMsg,
                subject: 'Account Block Appeal',
            });
            setContactSent(true);
            setShowContactForm(false);
        } catch (e) {
            alert(e.response?.data?.message || 'Failed to send. Please try again.');
        } finally {
            setContactLoading(false);
        }
    };

    const isDriver = role === 'driver';
    const isPA = role === 'pa';

    return (
        <div className="fixed inset-0 flex bg-slate-50 dark:bg-[#050510] overflow-hidden font-['Inter',sans-serif] transition-colors duration-500">
            {/* ── Left Panel: Cinematic Image Overlay ───────── */}
            <div className="hidden lg:flex lg:w-[45%] xl:w-[50%] relative group overflow-hidden">
                <div className="absolute inset-0 bg-slate-900 dark:bg-black transition-colors duration-500">
                    <img
                        src="/assets/login-bg.png"
                        alt="IdaCab Premium Transport"
                        className="w-full h-full object-cover opacity-[0.85] dark:opacity-40 grayscale-[10%] group-hover:scale-105 transition-transform duration-[20s] ease-out mix-blend-overlay"
                    />
                    {/* Cinematic Gradient Fade without washing out light mode */}
                    <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent transition-colors duration-500" />
                </div>

                {/* Content Overlay */}
                <div className="relative z-10 flex flex-col justify-between p-16 w-full h-full border-r border-slate-200 dark:border-white/5 transition-colors">
                    <div className="flex justify-between items-start">
                        <div className="bg-white/95 p-1.5 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] inline-flex items-center justify-center backdrop-blur-sm">
                            <img src="/assets/logo.png" alt="IdaCab Logo" className="h-16 sm:h-24 w-auto object-contain" />
                        </div>
                        <div className="px-4 py-1.5 rounded-full bg-white/10 dark:bg-white/5 backdrop-blur-md border border-white/20 dark:border-white/10 text-white dark:text-white/90 text-[10px] font-black tracking-widest uppercase shadow-xl transition-all">
                            {isPA ? 'PA Portal' : isDriver ? 'Fleet Excellence' : 'Elite Transport'}
                        </div>
                    </div>

                    <div className="max-w-lg mb-12 transform group-hover:translate-x-2 transition-transform duration-700 ease-out">
                        <h1 className="text-6xl lg:text-7xl font-black text-white leading-[1.1] mb-6 tracking-tight drop-shadow-2xl">
                            The Next
                            <span className="block text-teal-400">Generation</span>
                        </h1>
                        <p className="text-white/90 text-lg leading-relaxed font-medium drop-shadow-md">
                            {isPA
                                ? 'Access your PA compliance portal. Upload documents and manage your profile with ease.'
                                : isDriver
                                    ? 'Access the UK’s most trusted driver console. Manage routes and private jobs with precision.'
                                    : 'Experience reliable, premium private transport. Book your next journey with IdaCab today.'
                            }
                        </p>
                    </div>

                    <div className="flex items-center gap-6 text-white/50 text-[11px] font-black tracking-widest uppercase">
                        <span className="flex items-center gap-2">Professionalism</span>
                        <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
                        <span className="flex items-center gap-2">Reliability</span>
                    </div>
                </div>
            </div>

            {/* ── Right Panel: Next-Gen Form (Scrollbar Hidden) ────────── */}
            <div className="flex-1 h-full relative selection:bg-teal-500/20 z-10 flex flex-col justify-center items-center p-4 sm:p-8 lg:p-20 overflow-hidden">
                
                {/* Mobile Background Overlay (Hidden on desktop) */}
                <div className="absolute inset-0 lg:hidden z-0">
                    <img src="/assets/login-bg.png" alt="Background" className="w-full h-full object-cover opacity-30 dark:opacity-20 grayscale-[20%]" />
                    <div className="absolute inset-0 bg-gradient-to-b from-slate-50/80 via-slate-50/90 to-slate-50 dark:from-[#050510]/80 dark:via-[#050510]/90 dark:to-[#050510] backdrop-blur-sm" />
                </div>

                {/* Global Theme Toggle */}
                <div className="absolute top-6 right-6 z-50">
                    <button
                        onClick={toggleTheme}
                        className="p-3 rounded-full bg-white/50 dark:bg-slate-800/50 backdrop-blur-xl text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-all border border-slate-200 dark:border-slate-700 hover:scale-105 active:scale-95 shadow-sm"
                        aria-label="Toggle theme"
                    >
                        {theme === 'light' ? (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        )}
                    </button>
                </div>

                {/* Main Glass Form Container */}
                <div className="w-full max-w-[460px] bg-white/80 dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2.5rem] p-5 sm:p-8 shadow-2xl shadow-slate-200 dark:shadow-black/50 border border-white/60 dark:border-white/5 transition-colors duration-500 relative mt-4 mb-4 sm:my-0 z-10">

                    <div className="mb-4 sm:mb-6 flex flex-col items-center lg:hidden">
                        <div className="bg-white/95 p-1.5 rounded-2xl shadow-lg inline-flex items-center justify-center">
                            <img src="/assets/logo.png" alt="IdaCab Logo" className="h-16 w-auto object-contain" />
                        </div>
                    </div>

                    <div className="text-center mb-4 sm:mb-6">
                        <h2 className="text-2xl sm:text-3xl font-black text-slate-800 dark:text-white tracking-tight mb-1 sm:mb-2 transition-colors">Portal Access</h2>
                        <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm font-bold transition-colors">Log in to your IdaCab workspace</p>
                    </div>

                    {/* Role Toggle: Driver / PA Portal */}
                    <div className="flex gap-2 p-1.5 mb-4 bg-slate-50 dark:bg-slate-950/50 rounded-xl border border-slate-200 dark:border-slate-800 backdrop-blur-sm transition-colors">
                        <button
                            onClick={() => handleRoleToggle('driver')}
                            className={`flex-1 py-3.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all duration-300 relative ${isDriver ? 'text-white shadow-md bg-slate-900 dark:bg-slate-800' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'}`}
                        >
                            <span className="relative z-10">Driver Portal</span>
                        </button>
                        <button
                            onClick={() => handleRoleToggle('pa')}
                            className={`flex-1 py-3.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all duration-300 relative ${isPA ? 'text-white shadow-md bg-slate-900 dark:bg-slate-800' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'}`}
                        >
                            <span className="relative z-10">PA Portal</span>
                        </button>
                    </div>



                    <form onSubmit={handleSubmit} noValidate className="space-y-5 relative z-10">
                        <div className="space-y-1.5 capitalize tracking-wide">
                            <label className="block text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.15em] ml-1">Email / Mobile</label>
                            <input
                                name="email" type="text" value={formData.email} onChange={handleChange}
                                className="w-full px-5 py-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:border-teal-500 focus:bg-white dark:focus:bg-slate-800 focus:ring-4 focus:ring-teal-500/10 outline-none transition-all text-sm font-medium shadow-sm hover:border-slate-200"
                                placeholder="name@email.com"
                            />
                        </div>

                        <div className="space-y-1.5 capitalize tracking-wide relative">
                            <div className="flex justify-between items-center mb-1">
                                <label className="block text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.15em] ml-1">Password</label>
                                <Link to={`/forgot-password?role=${role}`} className="text-[9px] font-black tracking-widest uppercase text-teal-500 hover:text-teal-600 dark:text-teal-400 dark:hover:text-teal-300 hidden sm:block">Forgot Password?</Link>
                            </div>
                            <div className="relative group">
                                <input
                                    name="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={handleChange}
                                    className="w-full px-5 py-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:border-teal-500 focus:bg-white dark:focus:bg-slate-800 focus:ring-4 focus:ring-teal-500/10 outline-none transition-all text-sm font-medium shadow-sm hover:border-slate-200"
                                    placeholder="••••••••"
                                />
                                <button type="button" onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 px-5 flex items-center text-[10px] font-black text-slate-500 dark:text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 uppercase tracking-widest transition-colors bg-white/0">
                                    {showPassword ? 'Hide' : 'Show'}
                                </button>
                            </div>
                        </div>

                        <button type="submit" disabled={isLoading}
                            className="w-full py-4 rounded-2xl bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white text-[11px] font-black uppercase tracking-[0.2em] shadow-lg shadow-teal-500/20 active:scale-[0.98] disabled:opacity-50 disabled:grayscale transition-all flex justify-center items-center h-[52px] relative overflow-hidden mt-4"
                        >
                            {isLoading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <span className="relative z-10">Access Portal</span>}
                        </button>
                    </form>

                    <div className="relative flex items-center py-6">
                        <div className="flex-grow border-t border-slate-200 dark:border-slate-800 transition-colors"></div>
                        <span className="flex-shrink-0 mx-4 text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">or continue with</span>
                        <div className="flex-grow border-t border-slate-200 dark:border-slate-800 transition-colors"></div>
                    </div>

                    <button type="button" onClick={handleGoogleSignIn} disabled={isLoading}
                        className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 lg:hover:-translate-y-0.5 text-slate-700 dark:text-slate-200 text-[10px] font-black uppercase tracking-[0.15em] transition-all disabled:opacity-50 shadow-sm"
                    >
                        <svg viewBox="0 0 24 24" className="w-5 h-5 drop-shadow-sm" xmlns="http://www.w3.org/2000/svg"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" /><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" /><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" /><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" /><path d="M1 1h22v22H1z" fill="none" /></svg>
                        Continue via Google
                    </button>

                    {/* Resurrected Create Account Link */}
                    <div className="mt-8 text-center pt-2">
                        <p className="text-slate-500 dark:text-slate-400 text-[11px] font-bold tracking-wide uppercase transition-colors">
                            New to IdaCab? <Link to={isDriver ? '/register?role=driver' : '/register'} className="text-teal-600 dark:text-teal-400 font-black hover:text-teal-700 dark:hover:text-teal-300 ml-1 underline-offset-4 hover:underline transition-colors mt-2 sm:mt-0 inline-block">Create Account</Link>
                        </p>
                    </div>

                    <div className="mt-4 text-center sm:hidden">
                        <Link to={`/forgot-password?role=${role}`} className="text-[10px] font-black text-slate-400 dark:text-slate-500 hover:text-teal-600 dark:hover:text-teal-400 uppercase tracking-widest transition-colors">
                            Forgot Password?
                        </Link>
                    </div>
                </div>
            </div>

            {/* ── Contact Admin Modal ── */}
            {showContactForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-900/40 dark:bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl shadow-2xl shadow-black/50 border border-slate-200 dark:border-slate-800 overflow-hidden animate-in zoom-in-95 duration-200 relative">
                        
                        <div className="absolute top-4 right-4">
                            <button onClick={() => setShowContactForm(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-800 dark:hover:text-white transition-colors">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>

                        <div className="p-6 sm:p-8">
                            <div className="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 flex items-center justify-center mb-5">
                                <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
                            </div>
                            <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight mb-1">Contact Admin</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">Send an appeal to the administration team regarding your blocked account.</p>

                            {!contactSent ? (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-[11px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">Your Name</label>
                                        <input value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Full name" className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none text-sm transition-all" />
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">Your Message <span className="text-rose-500">*</span></label>
                                        <textarea value={contactMsg} onChange={e => setContactMsg(e.target.value)} rows={4} placeholder="Explain your situation... The admin will respond to your email." className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none text-sm transition-all resize-none leading-relaxed" />
                                    </div>
                                    <button onClick={handleContactAdmin} disabled={contactLoading || !contactMsg.trim()} className="w-full py-3.5 mt-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-black uppercase tracking-widest transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:scale-[0.98]">
                                        {contactLoading ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Sending...</> : <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg> Submit Appeal</>}
                                    </button>
                                </div>
                            ) : (
                                <div className="text-center py-6">
                                    <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto mb-4 animate-in zoom-in duration-300">
                                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                    </div>
                                    <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Message Sent</h4>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed mb-6">Your appeal has been submitted successfully. The administration team will review your case and respond via email to <strong>{formData.email}</strong>.</p>
                                    <button onClick={() => setShowContactForm(false)} className="px-6 py-2.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-bold uppercase tracking-wider hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
                                        Close Window
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LoginPage;
